import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useRecruitmentData } from '../context/RecruitmentContext';
import { useServicesData } from '../context/ServicesContext';
import { useMetiersData } from '../context/MetiersContext';
import { useSocieteData } from '../context/SocieteContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './MobileMenuAccordion.css';

/**
 * MobileMenuAccordion
 * Composant accordéon autonome pour le menu mobile.
 * Props:
 * - onNavigate?: () => void  // appelé après un clic de lien
 */
export default function MobileMenuAccordion({ onNavigate }) {
  const [openKey, setOpenKey] = useState(null);
  const location = useLocation();
  const { categories: recruitmentCats, sections: recruitmentSections } = useRecruitmentData();
  const { categories: servicesCats, sections: servicesSections } = useServicesData();
  const { categories: metiersCats, sections: metiersSections } = useMetiersData();
  const { categories: societeCats, sections: societeSections } = useSocieteData();
  const { showContact } = useSiteSettings();

  // Ouvrir automatiquement la bonne section selon la route
  useEffect(() => {
    const p = location.pathname || '';
    if (p.startsWith('/services')) setOpenKey('services');
    else if (p.startsWith('/metiers')) setOpenKey('metiers');
    else if (p.startsWith('/recrutement')) setOpenKey('recrutement');
    else if (p.startsWith('/societe')) setOpenKey('societe');
    else setOpenKey(null);
  }, [location.pathname]);

  const toggle = (key) => setOpenKey((prev) => (prev === key ? null : key));
  const handleNav = () => { if (onNavigate) onNavigate(); };

  const isActive = (to) => {
    try {
      if (!to) return false;
      if (to.startsWith('/')) {
        const [path, hash] = to.split('#');
        if (path && location.pathname !== path) return false;
        if (!hash) return location.pathname === path;
        return (location.hash || '').replace(/^#/, '') === hash;
      }
      return false;
    } catch {
      return false;
    }
  };

  return (
    <nav className="mm-accordion" aria-label="Navigation mobile (accordéon)">
      <ul className="mm-list">
        <li className="mm-item">
          <Link className={`mm-link${location.pathname === '/' ? ' active' : ''}`} to="/" onClick={handleNav}>Accueil</Link>
        </li>

        <AccordionSection
          title="Services"
          to="/services"
          open={openKey === 'services'}
          onToggle={() => toggle('services')}
          onNavigate={handleNav}
        >
          <ul className="mm-sublist">
            {servicesCats.length > 0 ? (
              servicesCats.map(cat => {
                const catSections = servicesSections
                  .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                  .sort((a, b) => (a.order || 0) - (b.order || 0));

                return (
                  <React.Fragment key={cat.id}>
                    <li>
                      <Link
                        className={`mm-sublink${isActive(`/services#${cat.slug}`) ? ' active' : ''}`}
                        to={`/services#${cat.slug}`}
                        onClick={handleNav}
                        style={{ fontWeight: '600' }}
                      >
                        {cat.title}
                      </Link>
                    </li>
                    {catSections.map(s => (
                      <li key={s.id}>
                        <Link
                          className={`mm-sublink${isActive(`/services#${s.slug}`) ? ' active' : ''}`}
                          to={`/services#${s.slug}`}
                          onClick={handleNav}
                          style={{ paddingLeft: '1.5rem' }}
                        >
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              <li><Link className={`mm-sublink${isActive('/services') ? ' active' : ''}`} to="/services" onClick={handleNav}>Nos Services</Link></li>
            )}
          </ul>
        </AccordionSection>

        <AccordionSection
          title="Métiers"
          to="/metiers"
          open={openKey === 'metiers'}
          onToggle={() => toggle('metiers')}
          onNavigate={handleNav}
        >
          <ul className="mm-sublist">
            {metiersCats.length > 0 ? (
              metiersCats.map(cat => {
                const catSections = metiersSections
                  .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                  .sort((a, b) => (a.order || 0) - (b.order || 0));

                return (
                  <React.Fragment key={cat.id}>
                    <li>
                      <Link
                        className={`mm-sublink${isActive(`/metiers#${cat.slug}`) ? ' active' : ''}`}
                        to={`/metiers#${cat.slug}`}
                        onClick={handleNav}
                        style={{ fontWeight: '600' }}
                      >
                        {cat.title}
                      </Link>
                    </li>
                    {catSections.map(s => (
                      <li key={s.id}>
                        <Link
                          className={`mm-sublink${isActive(`/metiers#${s.slug}`) ? ' active' : ''}`}
                          to={`/metiers#${s.slug}`}
                          onClick={handleNav}
                          style={{ paddingLeft: '1.5rem' }}
                        >
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              <li><Link className={`mm-sublink${isActive('/metiers') ? ' active' : ''}`} to="/metiers" onClick={handleNav}>Nos Métiers</Link></li>
            )}
          </ul>
        </AccordionSection>

        <AccordionSection
          title="Recrutement"
          to="/recrutement"
          open={openKey === 'recrutement'}
          onToggle={() => toggle('recrutement')}
          onNavigate={handleNav}
        >
          <ul className="mm-sublist">
            {recruitmentCats.length > 0 ? (
              recruitmentCats.map(cat => {
                const catSections = recruitmentSections
                  .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                  .sort((a, b) => (a.order || 0) - (b.order || 0));

                return (
                  <React.Fragment key={cat.id}>
                    <li>
                      <Link
                        className={`mm-sublink${isActive(`/recrutement#${cat.slug}`) ? ' active' : ''}`}
                        to={`/recrutement#${cat.slug}`}
                        onClick={handleNav}
                        style={{ fontWeight: '600' }}
                      >
                        {cat.title}
                      </Link>
                    </li>
                    {catSections.map(s => (
                      <li key={s.id}>
                        <Link
                          className={`mm-sublink${isActive(`/recrutement#${s.slug}`) ? ' active' : ''}`}
                          to={`/recrutement#${s.slug}`}
                          onClick={handleNav}
                          style={{ paddingLeft: '1.5rem' }}
                        >
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              <li><Link className={`mm-sublink${isActive('/recrutement') ? ' active' : ''}`} to="/recrutement" onClick={handleNav}>Recrutement</Link></li>
            )}
          </ul>
        </AccordionSection>

        <AccordionSection
          title="Société"
          to="/societe"
          open={openKey === 'societe'}
          onToggle={() => toggle('societe')}
          onNavigate={handleNav}
        >
          <ul className="mm-sublist">
            {societeCats.length > 0 ? (
              societeCats.map(cat => {
                const catSections = societeSections
                  .filter(s => (typeof s.category === 'object' ? s.category?.id : s.category) === cat.id)
                  .sort((a, b) => (a.order || 0) - (b.order || 0));

                return (
                  <React.Fragment key={cat.id}>
                    <li>
                      <Link
                        className={`mm-sublink${isActive(`/societe#${cat.slug}`) ? ' active' : ''}`}
                        to={`/societe#${cat.slug}`}
                        onClick={handleNav}
                        style={{ fontWeight: '600' }}
                      >
                        {cat.title}
                      </Link>
                    </li>
                    {catSections.map(s => (
                      <li key={s.id}>
                        <Link
                          className={`mm-sublink${isActive(`/societe#${s.slug}`) ? ' active' : ''}`}
                          to={`/societe#${s.slug}`}
                          onClick={handleNav}
                          style={{ paddingLeft: '1.5rem' }}
                        >
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </React.Fragment>
                );
              })
            ) : (
              <li><Link className={`mm-sublink${isActive('/societe') ? ' active' : ''}`} to="/societe" onClick={handleNav}>Notre Société</Link></li>
            )}
          </ul>
        </AccordionSection>

        <li className="mm-item">
          <Link className={`mm-link${location.pathname === '/references' ? ' active' : ''}`} to="/references" onClick={handleNav}>Références</Link>
        </li>
        {showContact && (
          <li className="mm-item">
            <Link className={`mm-link${location.pathname === '/contact' ? ' active' : ''}`} to="/contact" onClick={handleNav}>Contact</Link>
          </li>
        )}
      </ul>
    </nav>
  );
}

function AccordionSection({ title, to, open, onToggle, onNavigate, children }) {
  return (
    <li className={`mm-item ${open ? 'open' : ''}`}>
      <div className="mm-header">
        <Link className="mm-link mm-link-top" to={to} onClick={onNavigate}>{title}</Link>
        <button type="button" className="mm-toggle-btn" aria-expanded={open} onClick={onToggle} aria-label={open ? 'Replier' : 'Déplier'}>
          <span className={`mm-chev ${open ? 'rot' : ''}`} aria-hidden>⌃</span>
        </button>
      </div>
      <div className="mm-panel" role="region">
        {children}
      </div>
    </li>
  );
}
